import { Suspense, lazy } from "react";
import { motion } from "framer-motion";
import { Youtube, Play, ArrowDown } from "lucide-react";

const ThreeScene = lazy(() => import("./ThreeScene"));

const CHANNEL = "https://www.youtube.com/@motivationunlockedforever?sub_confirmation=1";
const PLAYLIST = "https://www.youtube.com/playlist?list=PL7poE7ZOzGHjMJRoQlezve4dImpYXvfen";

const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};
const rise = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

export default function Hero() {
  return (
    <section
      id="top"
      className="relative min-h-screen flex items-center overflow-hidden"
      data-testid="hero-section"
    >
      <div className="light-bleed h-[520px] w-[520px] -top-40 -right-20 opacity-60" />
      <div className="light-bleed h-[360px] w-[360px] bottom-0 left-0 opacity-30" />
      <Suspense fallback={null}>
        <ThreeScene />
      </Suspense>

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-28 pb-20">
        <motion.div variants={stagger} initial="hidden" animate="show" className="max-w-3xl">
          <motion.img
            variants={rise}
            src="/logo-red.png"
            alt="Motivation Unlocked"
            className="h-24 sm:h-32 w-auto mb-6 -ml-2 drop-shadow-[0_0_30px_rgba(255,59,48,0.35)]"
            data-testid="hero-logo"
          />
          <motion.p variants={rise} className="overline text-blaze mb-6">
            Mindset · Discipline · Breakthrough
          </motion.p>
          <motion.h1
            variants={rise}
            className="font-heading font-900 tracking-tight leading-[0.95] text-5xl sm:text-7xl lg:text-8xl"
          >
            Motivation
            <br />
            <span className="text-blaze">Unlocked</span>
          </motion.h1>
          <motion.p
            variants={rise}
            className="mt-8 text-base sm:text-lg text-white/70 max-w-xl leading-relaxed"
          >
            Real, unfiltered conversations around mental strength, emotional growth,
            discipline, healing, and personal transformation. Hosted by Marc Black, CEO.
          </motion.p>

          <motion.div variants={rise} className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href={CHANNEL}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-blaze px-6 py-3.5 text-sm font-bold text-white transition-transform hover:scale-105 hover:blaze-glow"
              data-testid="hero-subscribe-btn"
            >
              <Youtube size={18} /> Subscribe on YouTube
            </a>
            <a
              href="#episodes"
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-white/10"
              data-testid="hero-episodes-btn"
            >
              <Play size={16} /> Watch Episodes
            </a>
          </motion.div>

          <motion.div variants={rise} className="mt-14 flex items-center gap-8">
            {[
              { k: "Season", v: "01" },
              { k: "Episodes", v: "13+" },
              { k: "Focus", v: "Growth" },
            ].map((s) => (
              <div key={s.k}>
                <div className="font-heading font-800 text-2xl">{s.v}</div>
                <div className="overline text-white/40 mt-1">{s.k}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      <a
        href="#episodes"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-white/40 hover:text-white transition-colors"
        aria-label="Scroll down"
      >
        <ArrowDown className="animate-bounce" size={22} />
      </a>
    </section>
  );
}
